import { Stack, Group, Box, Text, BreadCrumbCard, EmptyStateBox } from 'components'
import { IconArticleOff } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

interface SkillContentItemRaw {
  id: string
  name: string
  lv1Name?: string
  lv1_name?: string
  breadcrumb?: string
  color?: string
  cycles?: Array<{ id: string; name: string }> | string[]
  originalCodes?: string
  original_codes?: string
}

interface ProjectSkillsStandardsListProps {
  specificSkills: SkillContentItemRaw[]
  contents: SkillContentItemRaw[]
  showCyclesBadges?: boolean
}

const getLv1Name = (item: SkillContentItemRaw): string =>
  item.lv1Name ?? item.lv1_name ?? ''

const getBreadcrumb = (item: SkillContentItemRaw): string =>
  item.breadcrumb ?? ''

const getColor = (item: SkillContentItemRaw): string =>
  item.color ?? '#000000'

const getCycles = (item: SkillContentItemRaw): Array<{ id: string; name: string }> => {
  const cycles: Array<{ id: string; name: string } | string> = [...(item.cycles ?? [])]
  return cycles.filter((cycle): cycle is { id: string; name: string } => typeof cycle === 'object')
}

const extractBreadcrumbPart = (breadcrumb: string, position: 0 | 1): string => {
  if (!breadcrumb) return ''
  const parts = breadcrumb.split('>')
  const part = parts[position]
  return part?.trim() ?? ''
}

const buildBreadcrumbWithoutName = (item: SkillContentItemRaw): string => {
  const lv1Name = getLv1Name(item)
  const breadcrumb = getBreadcrumb(item)
  return `${lv1Name} > ${extractBreadcrumbPart(breadcrumb, 0)} > ${extractBreadcrumbPart(breadcrumb, 1)}`
}

const getOriginalCodes = (item: SkillContentItemRaw): string =>
  item.originalCodes ?? item.original_codes ?? ''

const hasValidBreadcrumb = (item: SkillContentItemRaw): boolean =>
  Boolean(getBreadcrumb(item))

const SkillContentCard = ({
  item,
  showCyclesBadges,
}: {
  item: SkillContentItemRaw
  showCyclesBadges?: boolean
}) => {
  const originalCodes = getOriginalCodes(item)

  return (
    <Stack spacing={4}>
      <Group spacing={4} align='flex-start'>
        <BreadCrumbCard
          showCyclesBadges={showCyclesBadges}
          cycles={getCycles(item)}
          breadcrumb={buildBreadcrumbWithoutName(item)}
          color={getColor(item)}
        />
      </Group>
      <Box>
        {originalCodes ? (
          <Text span fw='bold' size='sm'>
            {originalCodes} -{' '}
          </Text>
        ) : null}
        <Text span size='sm'>
          {item.name}
        </Text>
      </Box>
    </Stack>
  )
}

export const ProjectSkillsStandardsList = ({
  specificSkills,
  contents,
  showCyclesBadges,
}: ProjectSkillsStandardsListProps) => {
  const { t } = useTranslation()

  const safeSkills = specificSkills ?? []
  const safeContents = contents ?? []

  const validSkills = safeSkills.filter(hasValidBreadcrumb)
  const validContents = safeContents.filter(hasValidBreadcrumb)
  const isEmpty = validSkills.length === 0 && validContents.length === 0

  if (isEmpty) {
    return (
      <Stack spacing='sm'>
        <EmptyStateBox withBorder text={t('new_project_view.no_skills_standards')} icon={<IconArticleOff />} />
      </Stack>
    )
  }

  return (
    <Stack spacing='sm'>
      {validSkills.map(item => (
        <SkillContentCard key={item.id} item={item} showCyclesBadges={showCyclesBadges} />
      ))}
      {validContents.map(item => (
        <SkillContentCard key={item.id} item={item} showCyclesBadges={showCyclesBadges} />
      ))}
    </Stack>
  )
}
